import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  GoogleMap,
  MarkerF,
  OverlayView,
  OverlayViewF,
  useJsApiLoader,
} from "@react-google-maps/api";
import axios from "axios";
import "./style.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlugCircleXmark } from "@fortawesome/free-solid-svg-icons";
import OrderDetails from "../../pages/OrderManagement/components/OrderDetails";
import { UserContext } from "../../context/UserContext";
import OrderCard from "../../pages/OrderManagement/components/OrderCard";

const containerStyle = {
  width: "100%",
  height: "95%",
};

const options = {
  gestureHandling: "greedy",
  clickableIcons: false,
  streetViewControl: false,
  fullscreenControlOptions: false,
  mapTypeControl: false,
  styles: [
    { elementType: "labels", featureType: "poi.business", stylers: [{ visibility: "off" }] },
  ],
};

const status_map = {
  withdrawn: "está indo até o endereço de entrega",
  arrived: "está retirando o pedido na loja",
  accept: "está indo até a loja",
  return: "está retornando a loja",
  "WITHDRAWN": "está entregando alguns pedidos na região. Aguarde.",
  "ARRIVED": "está retirando o pedido na loja",
  "ACCEPT": "está indo até a loja",
  "RETURN": "está retornando a loja",
};

const Tracking = () => {
  const { token } = useParams();
  const [driver, setDriver] = useState(null);
  const [order, setOrder] = useState(null);
  const [orderGrouping, setOrderGrouping] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [error, setError] = useState(null);
  const [map, setMap] = useState(null);
  const { user } = useContext(UserContext);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAPS_APP_KEY || "",
  });

  useEffect(() => {
    document.title = `Rastreio | Motum`;
  }, []);

  useEffect(() => {
    const fetchDriverLocation = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_DELIVERIES_API}/driver/tracking/${token}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
          }
        );
        setDriver(res.data.driver);
        if (res.data.isOrderGrouping) {
          setOrderGrouping(res.data.order);
          setOrder(null);
        } else {
          setOrder(res.data.order);
          setOrderGrouping(null);
        }
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || "Erro ao buscar localização.");
        setDriver(null);
      }
    };

    fetchDriverLocation();
    const interval = setInterval(fetchDriverLocation, 10000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const getDestinations = () => {
      let points = [];

      if (order) {
        const isToStore = ["accept", "arrived", "return"].includes(order.orderDetails.status);
        const location = isToStore
          ? order.store.address.location.coordinates
          : order.requester.address.location.coordinates;

        points.push({
          lat: location[0],
          lng: location[1],
          status: order.orderDetails.status,
        });
      }

      if (orderGrouping) {
        orderGrouping.orders.forEach(o => {
          const isToStore = ["accept", "arrived", "return"].includes(o.orderDetails.status);
          const location = isToStore
            ? o.store.address.location.coordinates
            : o.requester.address.location.coordinates;

          points.push({
            lat: location[0],
            lng: location[1],
            status: o.orderDetails.status,
          });
        });
      }

      setDestinations(points);
    };

    getDestinations();
  }, [order, orderGrouping]);

  useEffect(() => {
    if (map && driver?.deviceLocation && destinations.length) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(driver.deviceLocation);
      destinations.forEach(dest => bounds.extend(dest));
      map.fitBounds(bounds);
    }
  }, [map, driver, destinations]);

  if (!isLoaded)
    return <div className="tracking-container"><p>Carregando mapa...</p></div>;
  if (error)
    return <div className="tracking-container"><FontAwesomeIcon icon={faPlugCircleXmark} /><p>{error}</p></div>;

  return (
    <div className="tracking-container">
      {user && (
        <div className="order-container">
          {order && (
            <OrderDetails
              order={order}
              setSpinner={() => { }}
              handleShowModal={() => { }}
            />
          )}
          {orderGrouping && 
            <div className="grouping-header">
              <div className="title">Agrupamento de {orderGrouping.orders.length} pedido(s)</div>
              <div className="subtitle">Clique na entrega para ver detalhes</div>
            </div>
          }
          {orderGrouping && orderGrouping.orders.map((o, index) => (
            <div key={o._id} className="accordion-item">
              <button
                className="accordion-toggle"
                onClick={() => setActiveAccordion((prev) => (prev === index ? null : index))}
              >
                <OrderCard order={o} acceptOrder={() => { }} handleShowModal={() => { }} handleGroupOrder={() => { }} showGroupCheck={false} />
              </button>
              {activeAccordion === index && (
                <div className="accordion-content">
                  <OrderDetails
                    order={o}
                    setSpinner={() => { }}
                    handleShowModal={() => { }}
                  />
                </div>
              )}
            </div>
          ))}

        </div>
      )}

      {driver && destinations.length > 0 && (
        <div className="map-container" style={user ? { width: '60%' } : { width: '100%' }}>
          <div className="title">
            {driver.deliveryman.name.split(" ")[0]}{" "}
            {order
              ? status_map[order.orderDetails.status]
              : status_map[orderGrouping.status]}
          </div>

          <GoogleMap
            mapContainerStyle={containerStyle}
            center={driver.deviceLocation}
            zoom={16}
            onLoad={(map) => setMap(map)}
            options={options}
          >
            <OverlayViewF
              position={driver.deviceLocation}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className="pulse-marker">
                <img
                  src="/delivery.svg"
                  alt="Entregador"
                  width={50}
                  height={50}
                  className="marker-image"
                />
              </div>
            </OverlayViewF>

            {destinations.map((dest, idx) => (
              <MarkerF
                key={idx}
                position={{ lat: dest.lat, lng: dest.lng }}
                title={`Destino ${idx + 1}`}
                icon={{
                  url: ["accept", "arrived", "return"].includes(dest.status)
                    ? "/shop_location_pin.svg"
                    : "/packet_point.svg",
                }}
              />
            ))}
          </GoogleMap>
        </div>
      )}
    </div>
  );
};

export default Tracking;
